// apps/mtd-itsa-web/src/components/base/select/combobox-dropdown.tsx

import type { ElementType, ReactNode, RefAttributes } from "react";
import { isValidElement, useCallback, useRef, useState } from "react";
import { SearchLg as SearchIcon, ChevronDown } from "@untitledui/icons";
import type {
    ComboBoxProps as AriaComboBoxProps,
    ListBoxProps as AriaListBoxProps,
} from "react-aria-components";
import {
    ComboBox as AriaComboBox,
    Input as AriaInput,
    ListBox as AriaListBox,
    ComboBoxStateContext,
    Button as AriaButton,
} from "react-aria-components";

import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { Popover } from "@/components/base/select/popover";
import {
    type CommonProps,
    SelectContext,
    type SelectItemType,
    sizes,
} from "@/components/base/select/select";
import { useResizeObserver } from "@/hooks/use-resize-observer";
import { cx } from "@/utils/cx";

type PlaceholderIconType =
    | ElementType<{ className?: string; "aria-hidden"?: boolean; "data-icon"?: true }>
    | ReactNode;

interface ComboBoxDropdownProps
    extends Omit<AriaComboBoxProps<SelectItemType>, "children">,
    RefAttributes<HTMLDivElement>,
    CommonProps {
    items?: Iterable<SelectItemType>;
    popoverClassName?: string;
    shortcutClassName?: string;
    children: AriaListBoxProps<SelectItemType>["children"];
    placeholderIcon?: PlaceholderIconType;
    placeholder?: string;
    size?: "sm" | "md";
}

export const ComboBoxDropdown = ({
    placeholder = "Select...",
    size = "sm",
    label,
    hint,
    tooltip,
    items,
    popoverClassName,
    shortcutClassName,
    children,
    placeholderIcon: PlaceholderIcon = ChevronDown,
    ...otherProps
}: ComboBoxDropdownProps) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [popoverWidth, setPopoverWidth] = useState("");

    const onResize = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPopoverWidth(rect.width + "px");
    }, []);

    useResizeObserver({
        ref: triggerRef,
        box: "border-box",
        onResize,
    });

    return (
        <SelectContext.Provider value={{ size }}>
            <AriaComboBox menuTrigger="input" items={items} {...otherProps}>
                {(comboRenderState) => (
                    <div className="flex flex-col gap-1.5">
                        {label && (
                            <Label isRequired={comboRenderState.isRequired} tooltip={tooltip}>
                                {label}
                            </Label>
                        )}

                        <ComboBoxStateContext.Consumer>
                            {(comboState) => {
                                const selected = comboState?.selectedItem?.value as SelectItemType | undefined;

                                const iconNode = isValidElement(PlaceholderIcon)
                                    ? PlaceholderIcon
                                    : PlaceholderIcon
                                        ? (() => {
                                            const Icon = PlaceholderIcon as ElementType<{
                                                className?: string;
                                                "aria-hidden"?: boolean;
                                                "data-icon"?: true;
                                            }>;
                                            return (
                                                <Icon
                                                    data-icon
                                                    aria-hidden
                                                    className="mr-2 size-5 text-fg-quaternary"
                                                />
                                            );
                                        })()
                                        : null;

                                return (
                                    <AriaButton
                                        ref={triggerRef}
                                        className={({ isFocusVisible, isDisabled }) =>
                                            cx(
                                                "relative flex w-full cursor-pointer items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset",
                                                (isFocusVisible || comboRenderState.isOpen) && "ring-2 ring-brand",
                                                isDisabled && "cursor-not-allowed bg-disabled_subtle text-disabled",
                                                sizes[size].root,
                                                shortcutClassName
                                            )
                                        }
                                    >
                                        {selected?.avatarUrl ? (
                                            <img
                                                src={selected.avatarUrl}
                                                alt={selected.label ?? ""}
                                                className="size-5 rounded-full"
                                            />
                                        ) : (
                                            iconNode
                                        )}

                                        <span
                                            className={cx(
                                                "flex-1 truncate text-md text-left",
                                                !selected && "text-placeholder"
                                            )}
                                        >
                                            {selected?.label ?? placeholder}
                                        </span>

                                        <ChevronDown
                                            aria-hidden
                                            className={cx(
                                                "ml-auto shrink-0 text-fg-quaternary",
                                                size === "sm" ? "size-4 stroke-[2.5px]" : "size-5"
                                            )}
                                        />
                                    </AriaButton>
                                );
                            }}
                        </ComboBoxStateContext.Consumer>

                        <Popover
                            size={size}
                            triggerRef={triggerRef}
                            style={{ width: popoverWidth }}
                            className={popoverClassName}
                        >
                            <div className="p-2">
                                <div className="relative mb-2 flex items-center">
                                    <SearchIcon className="pointer-events-none absolute left-2 size-5 text-fg-quaternary" />
                                    <AriaInput
                                        placeholder="Search..."
                                        className="w-full rounded-md border border-secondary bg-primary py-1.5 pl-9 pr-3 text-md text-primary outline-none ring-0 focus:border-brand"
                                    />
                                </div>

                                <AriaListBox className="size-full outline-hidden">{children}</AriaListBox>
                            </div>
                        </Popover>

                        {hint && <HintText isInvalid={comboRenderState.isInvalid}>{hint}</HintText>}
                    </div>
                )}
            </AriaComboBox>
        </SelectContext.Provider>
    );
};
